/**
 * ProvisioningPage — Sprint 5.0.
 *
 * Fluxo de provisionamento QR físico (Sonoff) → Cliente IXC →
 * Contrato → Login PPPoE → Vínculo (device_binding).
 *
 * Wizard sequencial em 5 passos:
 *   1. Ler QR (input manual ou camera futuramente)
 *   2. Buscar cliente IXC (search por id/nome/cpf)
 *   3. Selecionar contrato (de cliente_contrato)
 *   4. Selecionar login PPPoE (de radusuarios)
 *   5. Confirmar vínculo (POST /devices/bind)
 *
 * Botão extra "🔬 TESTAR IXC" mostra latência + auth status + raw.
 */
import { useEffect, useState } from "react";
import {
  createDeviceBinding,
  ixcCustomerContracts,
  ixcCustomerLogins,
  ixcCustomerSummary,
  ixcSearchCustomers,
  ixcTest,
  listInitialTemplates,
  previewInitialTemplate,
} from "../lib/api.js";
import { t } from "../i18n/index.js";
import BindingsTable from "./BindingsTable.jsx";
import PendingBindingsTable from "../components/PendingBindingsTable.jsx";
import AuthorizationQueue from "../components/AuthorizationQueue.jsx";
import QrScannerModal from "../components/QrScannerModal.jsx";

const STEPS = ["qr", "customer", "contract", "login", "confirm"];

export default function ProvisioningPage() {
  // Estado do wizard
  const [step, setStep] = useState("qr");

  // Passo 1: QR scan / digitação
  const [qrSerial, setQrSerial] = useState("");
  const [deviceId, setDeviceId] = useState("");   // opcional — pode ser preenchido depois do primeiro heartbeat
  // Sprint 5.0.4 — modal do scanner de QR pela câmera
  const [scannerOpen, setScannerOpen] = useState(false);

  // Passo 2: cliente
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchErr, setSearchErr] = useState(null);     // erro REAL (vermelho)
  const [searchInfo, setSearchInfo] = useState(null);   // info (amarelo, ex: noResults)
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Passo 3-4: contratos + logins (sumarizado do summary endpoint)
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryErr, setSummaryErr] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [logins, setLogins] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedLogin, setSelectedLogin] = useState(null);

  // Passo 5: bind
  const [binding, setBinding] = useState(false);
  const [bindErr, setBindErr] = useState(null);
  const [bindResult, setBindResult] = useState(null);

  // Sprint 5.0.6 — seleção de template inicial
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("residential");
  const [templatePreview, setTemplatePreview] = useState(null);
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);

  // Teste IXC
  const [testOpen, setTestOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Hotfix S5.0.1 — chave que força reload da BindingsTable após bind
  const [bindingsRefreshKey, setBindingsRefreshKey] = useState(0);
  // Sprint 5.2A — aba da listagem: 'all' (todos) | 'pending' (pendentes)
  const [listTab, setListTab] = useState("all");

  // ─────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────

  // Sprint 5.0.6 — carrega lista de templates iniciais uma vez
  useEffect(() => {
    const ac = new AbortController();
    listInitialTemplates({ signal: ac.signal })
      .then((data) => setTemplates(data.templates || []))
      .catch(() => {
        // silencioso — seletor mostra fallback se vazio
      });
    return () => ac.abort();
  }, []);

  const runSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    setSearchErr(null);
    setSearchInfo(null);
    setSearchResults([]);
    try {
      const data = await ixcSearchCustomers(searchQ.trim(), { limit: 20 });

      // Hotfix S506A — debug temporário (só em DEV) para diagnosticar
      // discrepâncias entre o que o backend envia e o que o front lê.
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.debug("[provisioning] search response:", data);
      }

      // Hotfix S506A — leitura defensiva do campo `results`.
      // O backend (IxcCustomerSearchOut) SEMPRE retorna { results,
      // count, query }. Aceitamos também `customers` como alias
      // defensivo caso alguma camada intermediária renomeie, e o
      // próprio array cru caso a resposta venha sem envelope.
      let results = [];
      if (Array.isArray(data)) {
        results = data;
      } else if (data && Array.isArray(data.results)) {
        results = data.results;
      } else if (data && Array.isArray(data.customers)) {
        results = data.customers;
      }

      // IMPORTANTE: seta os resultados UMA vez e NÃO limpa depois.
      setSearchResults(results);

      // searchInfo (amarelo) APENAS quando a busca retornou 0 itens.
      if (results.length === 0) {
        setSearchInfo(t("provisioning.noResults"));
      }
    } catch (e) {
      // searchErr (vermelho) APENAS no catch — erro REAL de rede/IXC.
      // Mostra a mensagem real do backend (ex: 'IXC auth: ...').
      setSearchErr(e?.message || t("provisioning.errSearch"));
    } finally {
      setSearching(false);
    }
  };

  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setLoadingSummary(true);
    setSummaryErr(null);
    setContracts([]);
    setLogins([]);
    try {
      const summary = await ixcCustomerSummary(customer.id);
      setContracts(summary.contracts || []);
      setLogins(summary.logins || []);
      setStep("contract");
    } catch (e) {
      setSummaryErr(e?.message || t("provisioning.errSummary"));
    } finally {
      setLoadingSummary(false);
    }
  };

  const selectContract = (contract) => {
    setSelectedContract(contract);
    // Auto-filtra logins do contrato selecionado (se id_contrato bate)
    const filtered = logins.filter(
      (l) => !contract.id || !l.id_contrato || String(l.id_contrato) === String(contract.id)
    );
    if (filtered.length === 1) {
      // Auto-seleciona se só sobra 1
      setSelectedLogin(filtered[0]);
    }
    setStep("login");
  };

  const selectLogin = (login) => {
    setSelectedLogin(login);
    setStep("confirm");
  };

  // Sprint 5.0.6 — abre preview dos comandos do template selecionado
  const openTemplatePreview = async () => {
    if (!selectedTemplate || selectedTemplate === "custom") return;
    setTemplatePreview(null);
    setTemplatePreviewOpen(true);
    try {
      const cid = selectedCustomer?.id || "0000";
      const data = await previewInitialTemplate(selectedTemplate, cid);
      setTemplatePreview(data);
    } catch (e) {
      setTemplatePreview({
        found: false,
        error: e?.message || String(e),
      });
    }
  };

  const confirmBind = async () => {
    if (!qrSerial || !selectedCustomer) return;
    setBinding(true);
    setBindErr(null);
    setBindResult(null);
    try {
      const payload = {
        qr_serial: qrSerial.trim(),
        device_id: deviceId.trim() || undefined,
        customer_id: selectedCustomer.id,
        contract_id: selectedContract?.id || undefined,
        radius_id: selectedLogin?.id || undefined,
        pppoe_login: selectedLogin?.login || undefined,
        raw_json: {
          customer: selectedCustomer,
          contract: selectedContract,
          radius: selectedLogin,
        },
        // Sprint 5.0.6 — template inicial selecionado
        template_id: selectedTemplate || undefined,
      };
      const result = await createDeviceBinding(payload);
      setBindResult(result);
      // Hotfix S5.0.1 — força reload da tabela de bindings
      setBindingsRefreshKey((k) => k + 1);
    } catch (e) {
      setBindErr(e?.message || t("provisioning.errBind"));
    } finally {
      setBinding(false);
    }
  };

  const resetWizard = () => {
    setStep("qr");
    setQrSerial("");
    setDeviceId("");
    setSearchQ("");
    setSearchResults([]);
    setSearchErr(null);
    setSearchInfo(null);
    setSelectedCustomer(null);
    setContracts([]);
    setLogins([]);
    setSelectedContract(null);
    setSelectedLogin(null);
    setBindErr(null);
    setBindResult(null);
    // Sprint 5.0.6 — volta o template para o padrão
    setSelectedTemplate("residential");
    setTemplatePreview(null);
    setTemplatePreviewOpen(false);
  };

  const runIxcTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await ixcTest();
      setTestResult(r);
    } catch (e) {
      setTestResult({
        ok: false,
        message: e?.message || String(e),
        auth_status: "error",
      });
    } finally {
      setTesting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight mb-1">
            {t("provisioning.title")}
          </h1>
          <p className="text-sm text-noc-muted">{t("provisioning.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setTestOpen(true);
            runIxcTest();
          }}
          className="text-xs px-3 py-1.5 rounded border border-noc-purple/30 text-noc-purple hover:bg-noc-purple/10"
        >
          🔬 {t("provisioning.btnTestIxc")}
        </button>
      </header>

      {/* Stepper */}
      <Stepper currentStep={step} />

      {/* Passo 1 — Ler QR */}
      {step === "qr" && (
        <section className="card-pad space-y-3">
          <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
            {t("provisioning.step1Title")}
          </h2>
          <p className="text-xs text-noc-muted/80">
            {t("provisioning.step1Help")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs text-noc-muted">
                {t("provisioning.qrSerial")}
              </span>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={qrSerial}
                  onChange={(e) => setQrSerial(e.target.value)}
                  placeholder="25082200178100"
                  className="flex-1 min-w-0 bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-sm"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setScannerOpen(true)}
                  className="flex-shrink-0 px-2.5 py-1 text-xs rounded border border-noc-accent/30 text-noc-accent hover:bg-noc-accent/10 whitespace-nowrap"
                  title={t("provisioning.scanQrTooltip")}
                >
                  📷 {t("provisioning.scanQrBtn")}
                </button>
              </div>
            </label>
            <label className="space-y-1">
              <span className="text-xs text-noc-muted">
                {t("provisioning.deviceIdOptional")}
              </span>
              <input
                type="text"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                placeholder="IPWD-XXXXXXXX"
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-sm"
              />
            </label>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setStep("customer")}
              disabled={!qrSerial.trim()}
              className="px-3 py-1.5 text-xs rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold disabled:opacity-40"
            >
              {t("provisioning.next")} →
            </button>
          </div>
        </section>
      )}

      {/* Passo 2 — Buscar Cliente */}
      {step === "customer" && (
        <section className="card-pad space-y-3">
          <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
            {t("provisioning.step2Title")}
          </h2>
          <p className="text-xs text-noc-muted/80">
            {t("provisioning.step2Help")}
          </p>
          <div className="flex gap-2">
            <input
              type="search"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder={t("provisioning.searchPlaceholder")}
              className="flex-1 bg-noc-bg border border-noc-border rounded px-2 py-1 text-sm"
              autoFocus
            />
            <button
              type="button"
              onClick={runSearch}
              disabled={searching || !searchQ.trim()}
              className="px-3 py-1.5 text-xs rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold disabled:opacity-40"
            >
              {searching ? t("common.loading") : t("provisioning.searchBtn")}
            </button>
          </div>
          {searchInfo && (
            <div className="text-xs text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
              ℹ {searchInfo}
            </div>
          )}
          {searchErr && (
            <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 space-y-1">
              <div className="font-semibold">
                ✗ {t("provisioning.errSearchPrefix")}
              </div>
              <div className="font-mono text-[11px] whitespace-pre-wrap break-words">
                {searchErr}
              </div>
            </div>
          )}
          {searchResults.length > 0 && (
            <div className="space-y-1.5">
              {searchResults.map((c) => (
                <CustomerCard
                  key={c.id}
                  customer={c}
                  onSelect={() => selectCustomer(c)}
                />
              ))}
            </div>
          )}
          {loadingSummary && (
            <div className="text-xs text-noc-muted">{t("common.loading")}</div>
          )}
          {summaryErr && (
            <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
              {summaryErr}
            </div>
          )}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setStep("qr")}
              className="px-3 py-1.5 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text"
            >
              ← {t("provisioning.back")}
            </button>
          </div>
        </section>
      )}

      {/* Passo 3 — Contrato */}
      {step === "contract" && (
        <section className="card-pad space-y-3">
          <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
            {t("provisioning.step3Title")}
          </h2>
          <CustomerSummaryBox customer={selectedCustomer} />
          <div className="space-y-1.5 mt-2">
            {contracts.length === 0 ? (
              <div className="text-xs text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
                ⚠ {t("provisioning.noContracts")}
              </div>
            ) : (
              contracts.map((c) => (
                <ContractCard
                  key={c.id}
                  contract={c}
                  selected={selectedContract?.id === c.id}
                  onSelect={() => selectContract(c)}
                />
              ))
            )}
          </div>
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setStep("customer")}
              className="px-3 py-1.5 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text"
            >
              ← {t("provisioning.back")}
            </button>
            <button
              type="button"
              onClick={() => setStep("login")}
              disabled={!selectedContract && contracts.length > 0}
              className="px-3 py-1.5 text-xs rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold disabled:opacity-40"
            >
              {t("provisioning.next")} →
            </button>
          </div>
        </section>
      )}

      {/* Passo 4 — Login PPPoE */}
      {step === "login" && (
        <section className="card-pad space-y-3">
          <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
            {t("provisioning.step4Title")}
          </h2>
          <CustomerSummaryBox customer={selectedCustomer} />
          {selectedContract && (
            <div className="text-[11px] text-noc-muted">
              {t("provisioning.contractLabel")}:{" "}
              <span className="font-mono">{selectedContract.contrato || selectedContract.id}</span>
            </div>
          )}
          <div className="space-y-1.5 mt-2">
            {(() => {
              const filtered = selectedContract
                ? logins.filter(
                    (l) => !l.id_contrato || String(l.id_contrato) === String(selectedContract.id)
                  )
                : logins;
              if (filtered.length === 0) {
                return (
                  <div className="text-xs text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
                    ⚠ {t("provisioning.noLogins")}
                  </div>
                );
              }
              return filtered.map((l) => (
                <LoginCard
                  key={l.id}
                  login={l}
                  selected={selectedLogin?.id === l.id}
                  onSelect={() => selectLogin(l)}
                />
              ));
            })()}
          </div>
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setStep("contract")}
              className="px-3 py-1.5 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text"
            >
              ← {t("provisioning.back")}
            </button>
            <button
              type="button"
              onClick={() => setStep("confirm")}
              className="px-3 py-1.5 text-xs rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold"
            >
              {t("provisioning.next")} →
            </button>
          </div>
        </section>
      )}

      {/* Passo 5 — Confirmar vínculo */}
      {step === "confirm" && (
        <section className="card-pad space-y-3">
          <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
            {t("provisioning.step5Title")}
          </h2>
          <ConfirmationSummary
            qrSerial={qrSerial}
            deviceId={deviceId}
            customer={selectedCustomer}
            contract={selectedContract}
            login={selectedLogin}
          />

          {/* Sprint 5.0.6 — Seletor de template inicial */}
          {!bindResult && (
            <TemplateSelector
              templates={templates}
              selected={selectedTemplate}
              onSelect={setSelectedTemplate}
              onPreview={openTemplatePreview}
            />
          )}
          {bindErr && (
            <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
              {bindErr}
            </div>
          )}
          {bindResult && (
            <div className="space-y-2">
              {/* Status do vínculo */}
              <div className="text-xs text-noc-ok bg-noc-ok/10 border border-noc-ok/30 rounded p-2">
                ✓ {t("provisioning.bindSuccess")}{" "}
                <span className="font-mono">{bindResult.binding?.id}</span>
              </div>

              {/* Sprint 5.0.3 — Status do device (encontrado / aguardando) */}
              {bindResult.device_exists ? (
                <div className="text-[11px] text-noc-ok bg-noc-ok/10 border border-noc-ok/30 rounded p-2">
                  ● {t("provisioning.deviceFound")}{" "}
                  <span className="font-mono">
                    {bindResult.binding?.device_id}
                  </span>
                </div>
              ) : (
                <div className="text-[11px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
                  ⏳ {t("provisioning.deviceWaiting")}{" "}
                  {bindResult.binding?.device_id && (
                    <span className="font-mono">
                      ({bindResult.binding.device_id})
                    </span>
                  )}
                </div>
              )}

              {/* Sprint 5.0.3 — Localização */}
              {bindResult.location_updated ? (
                <div className="text-[11px] text-noc-accent bg-noc-accent/10 border border-noc-accent/30 rounded p-2">
                  📍 {t("provisioning.locationUpdated")}{" "}
                  <span className="text-noc-muted/80">
                    ({t(`provisioning.locationSource.${bindResult.location_source}`)}
                    {bindResult.latitude != null && bindResult.longitude != null && (
                      <>
                        {" · "}
                        <span className="font-mono">
                          {bindResult.latitude.toFixed(4)},{" "}
                          {bindResult.longitude.toFixed(4)}
                        </span>
                      </>
                    )}
                    )
                  </span>
                </div>
              ) : (
                <div className="text-[11px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
                  ⚠ {t("provisioning.locationMissing")}
                </div>
              )}

              {/* Warnings adicionais — só mostra os não-mapeados acima */}
              {(bindResult.warnings || []).filter(
                (w) =>
                  w !== "device_not_found" &&
                  w !== "ixc_no_coordinates"
              ).length > 0 && (
                <div className="text-[10px] text-noc-warn italic">
                  {t("provisioning.otherWarnings")}:{" "}
                  {(bindResult.warnings || [])
                    .filter(
                      (w) => w !== "device_not_found" && w !== "ixc_no_coordinates"
                    )
                    .join(", ")}
                </div>
              )}

              {/* Sprint 5.0.6 — Resultado da aplicação do template */}
              {bindResult.template && !bindResult.template.skipped && (
                <div className="text-[11px] text-noc-accent bg-noc-accent/10 border border-noc-accent/30 rounded p-2">
                  🎛 {t("provisioning.templateApplied")}{" "}
                  <span className="font-semibold">
                    {bindResult.template.template_name}
                  </span>
                  {" — "}
                  {t("provisioning.templateCommandsEnqueued", {
                    n: bindResult.template.commands_enqueued?.length || 0,
                  })}
                </div>
              )}
              {bindResult.template && bindResult.template.skipped && (
                <div className="text-[11px] text-noc-muted/80 bg-noc-bg/40 border border-noc-border/50 rounded p-2">
                  🎛 {t("provisioning.templateSkipped")}
                </div>
              )}
              {(bindResult.warnings || []).includes(
                "template_skipped_no_device"
              ) && (
                <div className="text-[11px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
                  ⚠ {t("provisioning.templateSkippedNoDevice")}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setStep("login")}
              disabled={binding}
              className="px-3 py-1.5 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text disabled:opacity-40"
            >
              ← {t("provisioning.back")}
            </button>
            {bindResult ? (
              <button
                type="button"
                onClick={resetWizard}
                className="px-3 py-1.5 text-xs rounded bg-noc-ok hover:brightness-110 text-noc-bg font-semibold"
              >
                {t("provisioning.newBinding")}
              </button>
            ) : (
              <button
                type="button"
                onClick={confirmBind}
                disabled={binding}
                className="px-3 py-1.5 text-xs rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold disabled:opacity-40"
              >
                {binding ? t("provisioning.binding") : t("provisioning.confirmBind")}
              </button>
            )}
          </div>
        </section>
      )}

      {/* Modal TESTAR IXC */}
      {testOpen && (
        <TestIxcModal
          testing={testing}
          result={testResult}
          onClose={() => setTestOpen(false)}
          onRetry={runIxcTest}
        />
      )}

      {/* Sprint 5.0.4 — Modal scanner de QR pela câmera */}
      {scannerOpen && (
        <QrScannerModal
          onDetected={(value) => {
            setQrSerial(value);
            setScannerOpen(false);
            // QR lido com sucesso → avança direto para busca do cliente
            setStep("customer");
          }}
          onManual={() => setScannerOpen(false)}
          onClose={() => setScannerOpen(false)}
        />
      )}

      {/* Sprint 5.0.6 — Modal preview do template */}
      {templatePreviewOpen && (
        <TemplatePreviewModal
          preview={templatePreview}
          onClose={() => setTemplatePreviewOpen(false)}
        />
      )}

      {/* Sprint 5.2A — Abas da listagem: Todos / Pendentes */}
      <div className="flex gap-1 border-b border-noc-border">
        <button
          type="button"
          onClick={() => setListTab("all")}
          className={`px-3 py-1.5 text-xs rounded-t border-b-2 transition-colors ${
            listTab === "all"
              ? "border-noc-accent/30 text-noc-accent"
              : "border-transparent text-noc-muted hover:text-noc-text"
          }`}
        >
          {t("provisioning.tabAll")}
        </button>
        <button
          type="button"
          onClick={() => setListTab("pending")}
          className={`px-3 py-1.5 text-xs rounded-t border-b-2 transition-colors ${
            listTab === "pending"
              ? "border-noc-warn/30 text-noc-warn"
              : "border-transparent text-noc-muted hover:text-noc-text"
          }`}
        >
          {t("provisioning.tabPending")}
        </button>
        <button
          type="button"
          onClick={() => setListTab("authq")}
          className={`px-3 py-1.5 text-xs rounded-t border-b-2 transition-colors ${
            listTab === "authq"
              ? "border-noc-ok/30 text-noc-ok"
              : "border-transparent text-noc-muted hover:text-noc-text"
          }`}
        >
          Aguardando autorização
        </button>
      </div>

      {/* Hotfix S5.0.1 / Sprint 5.2A — Tabela conforme aba ativa.
          S3.11C — aba "Aguardando autorização" (gate de device pendente). */}
      {listTab === "all" ? (
        <BindingsTable refreshKey={bindingsRefreshKey} />
      ) : listTab === "authq" ? (
        <AuthorizationQueue />
      ) : (
        <PendingBindingsTable refreshKey={bindingsRefreshKey} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stepper
// ─────────────────────────────────────────────────────────────────────
function Stepper({ currentStep }) {
  const labels = {
    qr: t("provisioning.stepQr"),
    customer: t("provisioning.stepCustomer"),
    contract: t("provisioning.stepContract"),
    login: t("provisioning.stepLogin"),
    confirm: t("provisioning.stepConfirm"),
  };
  const currentIdx = STEPS.indexOf(currentStep);
  return (
    <div className="flex items-center gap-2 text-[11px] overflow-x-auto pb-1">
      {STEPS.map((s, i) => {
        const active = s === currentStep;
        const done = i < currentIdx;
        return (
          <div key={s} className="flex items-center gap-2 flex-shrink-0">
            <span
              className={`px-2 py-0.5 rounded border whitespace-nowrap ${
                active
                  ? "border-noc-accent/30 bg-noc-accent/10 text-noc-accent font-semibold"
                  : done
                  ? "border-noc-ok/30 text-noc-ok"
                  : "border-noc-border text-noc-muted"
              }`}
            >
              {done && "✓ "}{i + 1}. {labels[s]}
            </span>
            {i < STEPS.length - 1 && (
              <span className="text-noc-muted/60">→</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// CustomerCard / ContractCard / LoginCard / CustomerSummaryBox /
// ConfirmationSummary
// ─────────────────────────────────────────────────────────────────────
function CustomerCard({ customer, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full text-left p-2 rounded border border-noc-border/60 bg-noc-bg/30 hover:border-noc-accent/30 hover:bg-noc-bg/50 text-xs transition-colors"
    >
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-noc-accent">#{customer.id}</span>
        <span className="font-semibold flex-1 truncate">{customer.razao}</span>
      </div>
      <div className="text-[10px] text-noc-muted mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
        {customer.cnpj_cpf && <span>{customer.cnpj_cpf}</span>}
        {customer.cidade && <span>{customer.cidade}</span>}
        {customer.telefone_celular && (
          <span className="font-mono">{customer.telefone_celular}</span>
        )}
      </div>
    </button>
  );
}

function ContractCard({ contract, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-2 rounded border text-xs transition-colors ${
        selected
          ? "border-noc-accent/30 bg-noc-accent/10"
          : "border-noc-border/60 bg-noc-bg/30 hover:border-noc-accent/30"
      }`}
    >
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-noc-accent">#{contract.id}</span>
        <span className="font-semibold flex-1 truncate">
          {contract.contrato || t("provisioning.noContractName")}
        </span>
        {contract.status && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-noc-border/60 uppercase">
            {contract.status}
          </span>
        )}
      </div>
      <div className="text-[10px] text-noc-muted mt-0.5 flex flex-wrap gap-x-3">
        {contract.tipo && <span>tipo: {contract.tipo}</span>}
        {contract.status_internet && (
          <span>internet: {contract.status_internet}</span>
        )}
      </div>
    </button>
  );
}

function LoginCard({ login, selected, onSelect }) {
  const isOnline = login.online === "S";
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-2 rounded border text-xs transition-colors ${
        selected
          ? "border-noc-accent/30 bg-noc-accent/10"
          : "border-noc-border/60 bg-noc-bg/30 hover:border-noc-accent/30"
      }`}
    >
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-noc-accent">#{login.id}</span>
        <span className="font-semibold flex-1 truncate font-mono">
          {login.login || t("provisioning.noLoginName")}
        </span>
        <span
          className={`inline-block w-2 h-2 rounded-full ${
            isOnline ? "bg-noc-ok/10" : "bg-noc-muted"
          }`}
          title={isOnline ? "online" : "offline"}
        />
      </div>
      <div className="text-[10px] text-noc-muted mt-0.5 flex flex-wrap gap-x-3 font-mono">
        {login.ip && <span>IP: {login.ip}</span>}
        {login.mac && <span>MAC: {login.mac}</span>}
        {login.vlan && <span>VLAN: {login.vlan}</span>}
      </div>
    </button>
  );
}

function CustomerSummaryBox({ customer }) {
  if (!customer) return null;
  return (
    <div className="bg-noc-bg/40 border border-noc-accent/30 rounded p-2 text-xs space-y-0.5">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-noc-accent">#{customer.id}</span>
        <span className="font-semibold">{customer.razao}</span>
      </div>
      {customer.cidade && (
        <div className="text-[10px] text-noc-muted">{customer.cidade}</div>
      )}
    </div>
  );
}

function ConfirmationSummary({ qrSerial, deviceId, customer, contract, login }) {
  return (
    <div className="bg-noc-bg/40 border border-noc-accent/30 rounded p-3 text-xs space-y-2">
      <Row label={t("provisioning.qrSerial")} value={qrSerial} mono />
      {deviceId && <Row label={t("provisioning.deviceIdLabel")} value={deviceId} mono />}
      <Row
        label={t("provisioning.customerLabel")}
        value={customer ? `#${customer.id} · ${customer.razao}` : "—"}
      />
      {contract && (
        <Row
          label={t("provisioning.contractLabel")}
          value={`#${contract.id} · ${contract.contrato || "—"}`}
        />
      )}
      {login && (
        <>
          <Row
            label={t("provisioning.loginLabel")}
            value={`#${login.id} · ${login.login || "—"}`}
            mono
          />
          {login.online === "S" && (
            <Row
              label={t("provisioning.onlineLabel")}
              value="● online"
              valueClass="text-noc-ok"
            />
          )}
          {login.ip && <Row label="IP" value={login.ip} mono />}
          {login.mac && <Row label="MAC" value={login.mac} mono />}
          {login.vlan && <Row label="VLAN" value={login.vlan} mono />}
        </>
      )}
    </div>
  );
}

function Row({ label, value, mono, valueClass }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <span className="text-noc-muted col-span-1">{label}:</span>
      <span className={`col-span-2 ${mono ? "font-mono" : ""} ${valueClass || ""}`}>
        {value}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Modal TESTAR IXC
// ─────────────────────────────────────────────────────────────────────
function TestIxcModal({ testing, result, onClose, onRetry }) {
  return (
    <div
      className="fixed inset-0 z-[2200] bg-black/80 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-noc-panel border border-noc-purple/30 rounded shadow-2xl w-full max-w-2xl flex flex-col"
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-noc-border">
          <h3 className="text-sm font-semibold text-noc-purple">
            🔬 {t("provisioning.testIxcTitle")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-noc-muted hover:text-noc-text text-xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3 text-xs">
          {testing && (
            <div className="text-noc-muted">{t("provisioning.testing")}</div>
          )}
          {result && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-noc-bg/40 border border-noc-border/60 rounded p-2">
                  <div className="text-[10px] uppercase text-noc-muted">
                    {t("provisioning.testStatus")}
                  </div>
                  <div
                    className={`text-base font-semibold ${
                      result.ok ? "text-noc-ok" : "text-noc-err"
                    }`}
                  >
                    {result.ok ? "✓ OK" : "✗ FALHOU"}
                  </div>
                </div>
                <div className="bg-noc-bg/40 border border-noc-border/60 rounded p-2">
                  <div className="text-[10px] uppercase text-noc-muted">
                    {t("provisioning.testLatency")}
                  </div>
                  <div className="text-base font-mono">
                    {result.latency_ms ?? "—"} ms
                  </div>
                </div>
              </div>
              <Row
                label={t("provisioning.testAuthStatus")}
                value={result.auth_status || "—"}
                mono
              />
              {result.base_url && (
                <Row label="base_url" value={result.base_url} mono />
              )}
              {result.message && (
                <div className="bg-noc-bg/40 border border-noc-border/60 rounded p-2 text-noc-muted">
                  {result.message}
                </div>
              )}
              {result.server_info && (
                <details className="bg-noc-bg/40 border border-noc-border/60 rounded">
                  <summary className="px-2 py-1 cursor-pointer text-noc-muted text-[10px] uppercase">
                    {t("provisioning.testServerInfo")}
                  </summary>
                  <pre className="text-[10px] font-mono p-2 overflow-x-auto max-h-32">
                    {JSON.stringify(result.server_info, null, 2)}
                  </pre>
                </details>
              )}
              <details className="bg-noc-bg/40 border border-noc-border/60 rounded">
                <summary className="px-2 py-1 cursor-pointer text-noc-muted text-[10px] uppercase">
                  {t("provisioning.testRawOutput")}
                </summary>
                <pre className="text-[10px] font-mono p-2 overflow-x-auto max-h-48">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-noc-border flex justify-between">
          <button
            type="button"
            onClick={onRetry}
            disabled={testing}
            className="px-3 py-1.5 text-xs rounded border border-noc-purple/30 text-noc-purple hover:bg-noc-purple/10 disabled:opacity-40"
          >
            🔄 {t("provisioning.testRetry")}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text"
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sprint 5.0.6 — Seletor de template inicial
// ─────────────────────────────────────────────────────────────────────
function TemplateSelector({ templates, selected, onSelect, onPreview }) {
  // Fallback: se a API ainda não respondeu, mostra os 3 padrão
  const list = templates.length > 0
    ? templates
    : [
        { id: "residential", name: "Residencial", description: "" },
        { id: "business", name: "Empresarial", description: "" },
        { id: "custom", name: "Personalizado", description: "" },
      ];

  return (
    <div className="space-y-2">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint">
        {t("provisioning.templateSectionTitle")}
      </div>
      <div className="text-[10px] text-noc-muted/70">
        {t("provisioning.templateSectionHelp")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {list.map((tpl) => {
          const active = selected === tpl.id;
          return (
            <button
              key={tpl.id}
              type="button"
              onClick={() => onSelect(tpl.id)}
              className={`text-left p-2 rounded border text-xs transition-colors ${
                active
                  ? "border-noc-accent/30 bg-noc-accent/10"
                  : "border-noc-border/60 bg-noc-bg/30 hover:border-noc-accent/30"
              }`}
            >
              <div className="flex items-center gap-1.5">
                <span
                  className={`inline-block w-2.5 h-2.5 rounded-full border ${
                    active
                      ? "bg-noc-accent/10 border-noc-accent/30"
                      : "border-noc-muted"
                  }`}
                />
                <span className="font-semibold">{tpl.name}</span>
              </div>
              {tpl.description && (
                <div className="text-[10px] text-noc-muted/80 mt-1 leading-snug">
                  {tpl.description}
                </div>
              )}
            </button>
          );
        })}
      </div>
      {selected && selected !== "custom" && (
        <button
          type="button"
          onClick={onPreview}
          className="text-[11px] text-noc-accent hover:text-noc-accent underline"
        >
          🔍 {t("provisioning.templatePreviewBtn")}
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sprint 5.0.6 — Modal de preview dos comandos do template
// ─────────────────────────────────────────────────────────────────────
function TemplatePreviewModal({ preview, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[2250] bg-black/80 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-noc-panel border border-noc-accent/30 rounded shadow-2xl w-full max-w-lg flex flex-col"
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-noc-border">
          <h3 className="text-sm font-semibold text-noc-accent">
            🔍 {t("provisioning.templatePreviewTitle")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-noc-muted hover:text-noc-text text-xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3 text-xs">
          {!preview && (
            <div className="text-noc-muted">{t("common.loading")}</div>
          )}
          {preview && !preview.found && (
            <div className="text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
              {preview.error || t("provisioning.templateNotFound")}
            </div>
          )}
          {preview && preview.found && (
            <>
              <div>
                <span className="font-semibold text-noc-text">
                  {preview.template_name}
                </span>
                {preview.description && (
                  <p className="text-noc-muted/80 mt-1">
                    {preview.description}
                  </p>
                )}
              </div>
              <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-noc-faint">
                {t("provisioning.templateCommandsLabel")}
              </div>
              {Object.entries(preview.commands || {}).map(([type, payload]) => (
                <details
                  key={type}
                  className="bg-noc-bg/40 border border-noc-border/60 rounded"
                  open
                >
                  <summary className="px-2 py-1 cursor-pointer font-mono text-noc-accent text-[11px]">
                    {type}
                  </summary>
                  <pre className="text-[10px] font-mono p-2 overflow-x-auto max-h-48 text-noc-text">
                    {JSON.stringify(payload, null, 2)}
                  </pre>
                </details>
              ))}
              {Object.keys(preview.commands || {}).length === 0 && (
                <div className="font-mono text-[12px] text-noc-faint">
                  {t("provisioning.templateNoCommands")}
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-noc-border flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text"
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
